import React, { useEffect, useState } from 'react';
import { useSearchParams, useParams, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { createPageUrl } from '../utils';
import { useAuth } from '@/contexts/AuthContext';

import OverviewSection from '@/components/portal/sections/OverviewSection';
import ProductionSection from '@/components/portal/sections/ProductionSection';
import ScheduleSection from '@/components/portal/sections/ScheduleSection';
import CrewSection from '@/components/portal/sections/CrewSection';
import TeamSection from '@/components/portal/sections/TeamSection';
import TravelSection from '@/components/portal/sections/TravelSection';
import VenueSection from '@/components/portal/sections/VenueSection';
import TechnicalSection from '@/components/portal/sections/TechnicalSection';
import SpeakersSection from '@/components/portal/sections/SpeakersSection';
import BrandingSection from '@/components/portal/sections/BrandingSection';
import OnsiteSection from '@/components/portal/sections/OnsiteSection';
import ChangeLogSection from '@/components/portal/sections/ChangeLogSection';
import ResourcesSection from '@/components/portal/sections/ResourcesSection';
import AccessControl from '@/components/portal/AccessControl';
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { usePermissions } from '@/components/portal/usePermissions';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  MapPin,
  Monitor,
  Mic2,
  Palette,
  Info,
  FileText,
  Settings,
  Menu,
  X,
  ChevronDown,
  LogOut,
  User,
  BookOpen,
} from 'lucide-react';
import { cn } from "@/lib/utils";

const navSections = [
  { name: 'Venue', icon: MapPin, section: 'venue' },
  { name: 'Speakers', icon: Mic2, section: 'speakers' },
  { name: 'Branding', icon: Palette, section: 'branding' },
  { name: 'On-Site Info', icon: Info, section: 'onsite' },
  { name: 'Change Log', icon: FileText, section: 'changelog' },
  { name: 'Resources', icon: BookOpen, section: 'resources' },
  { name: 'Access & Sharing', icon: Settings, section: 'access', adminOnly: true },
];

export default function EventPortal() {
  const [searchParams] = useSearchParams();
  const params = useParams();
  const { user, profile, loading: authLoading } = useAuth();
  const eventId = params.eventId || searchParams.get('eventId');
  const section = searchParams.get('section') || 'overview';
  const [event, setEvent] = useState<any>(null);
  const [eventLoading, setEventLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (eventId) loadEvent();
  }, [eventId]);

  // Reload event when page becomes visible (e.g., navigating back from edit)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && eventId) {
        loadEvent();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [eventId]);

  const loadEvent = async () => {
    try {
      console.log('[EventPortal] Loading event:', eventId);
      console.log('[EventPortal] Current user:', user);

      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('id', eventId)
        .maybeSingle();

      console.log('[EventPortal] Query result:', { data, error });

      if (error) throw error;
      if (data) {
        if (typeof data.venues === 'string') {
          data.venues = JSON.parse(data.venues || '[]');
        }
        setEvent(data);
      } else {
        console.error('[EventPortal] Event query returned null - likely RLS policy blocking access');
      }
    } catch (error) {
      console.error('[EventPortal] Error loading event:', error);
    } finally {
      setEventLoading(false);
    }
  };

  const { permissions, loading: permissionsLoading } = usePermissions(eventId);

  if (!eventId) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-zinc-500">No event selected</p>
      </div>
    );
  }

  const isLoading = authLoading || eventLoading || permissionsLoading;

  const sectionComponents: Record<string, React.ComponentType<any>> = {
    overview: OverviewSection,
    production: ProductionSection,
    schedule: ScheduleSection,
    crew: CrewSection,
    team: TeamSection,
    travel: TravelSection,
    venue: VenueSection,
    av_technical: TechnicalSection,
    speakers: SpeakersSection,
    branding: BrandingSection,
    onsite: OnsiteSection,
    changelog: ChangeLogSection,
    resources: ResourcesSection,
    access: AccessControl,
  };

  const SectionComponent = sectionComponents[section] || OverviewSection;

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex">
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside className={cn(
        "fixed top-0 left-0 z-50 w-64 h-screen bg-[#1a1a1a]/95 backdrop-blur-xl border-r border-zinc-800/50 flex flex-col transition-transform duration-300 ease-out",
        sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )}>
        <div className="p-4 border-b border-zinc-800/50">
          <div className="flex items-center justify-between">
            <Link to={createPageUrl('Home')} className="flex items-center gap-3">
              <img
                src="/slidepusher_events.svg"
                alt="Slidepusher Events"
                className="h-[60px]"
              />
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden text-zinc-400 hover:text-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        <div className="p-4 border-b border-zinc-800/50">
          {eventLoading ? (
            <div className="flex items-center gap-3">
              <Skeleton className="w-10 h-10 rounded-lg bg-zinc-800/50" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-3 w-20 bg-zinc-800/50" />
                <Skeleton className="h-4 w-32 bg-zinc-800/50" />
              </div>
            </div>
          ) : event ? (
            <div className="flex items-center gap-3">
              {event.avatar_url ? (
                <img
                  src={event.avatar_url}
                  alt={event.name}
                  className="w-10 h-10 rounded-lg object-cover border border-zinc-700"
                />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-zinc-700 flex items-center justify-center">
                  <span className="text-xs font-semibold text-white">
                    {event.name?.substring(0, 2).toUpperCase()}
                  </span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-0.5">Current Event</p>
                <p className="text-sm font-medium text-white truncate">{event.name}</p>
              </div>
            </div>
          ) : null}
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <div className="space-y-1 mb-6">
            <div className="px-3 py-2 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
              Main Sections
            </div>
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'production', label: 'Production' },
              { id: 'schedule', label: 'Schedule' },
              { id: 'team', label: 'Team' },
              { id: 'crew', label: 'Crew' },
            ].map((tab) => {
              const isActive = section === tab.id;
              return (
                <Link
                  key={tab.id}
                  to={`${createPageUrl('EventPortal')}?eventId=${eventId}&section=${tab.id}`}
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded text-sm font-medium transition-all duration-200",
                    isActive
                      ? "bg-primary-500 text-white"
                      : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"
                  )}
                >
                  {tab.label}
                </Link>
              );
            })}
          </div>

          <div className="space-y-1">
            <div className="px-3 py-2 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
              Additional
            </div>
            {navSections.map((item) => {
              const isActive = section === item.section;
              if (item.adminOnly && profile?.role !== 'admin') return null;

              const linkUrl = item.isExternalPage
                ? `${createPageUrl('Resources')}?eventId=${eventId}`
                : `${createPageUrl('EventPortal')}?eventId=${eventId}&section=${item.section}`;

              return (
                <Link
                  key={item.section || item.name}
                  to={linkUrl}
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded text-sm font-medium transition-all duration-200",
                    isActive
                      ? "bg-primary-500 text-white"
                      : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"
                  )}
                >
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  {item.name}
                </Link>
              );
            })}
          </div>
        </nav>

        <div className="p-4 border-t border-zinc-800/50">
          <Link
            to={createPageUrl('Events')}
            onClick={() => setSidebarOpen(false)}
            className="flex items-center gap-2 text-sm text-zinc-500 hover:text-white transition-colors px-3 py-2"
          >
            <ChevronDown className="w-4 h-4 rotate-90" />
            All Events
          </Link>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-h-screen w-full overflow-x-hidden lg:ml-64">
        <div className="fixed top-0 left-0 right-0 lg:left-64 z-40 bg-[#0a0a0a]/80 backdrop-blur-xl border-b border-zinc-800/50">
          <div className="flex items-center gap-3 h-14 px-4 lg:px-6 min-w-0">
            <Button
              variant="ghost"
              size="icon"
              className="text-zinc-400 hover:text-white flex-shrink-0 lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </Button>
            {eventLoading ? (
              <Skeleton className="h-5 w-32 bg-zinc-800/50" />
            ) : event ? (
              <div className="flex items-center gap-2 min-w-0 flex-1">
                {event.avatar_url ? (
                  <img
                    src={event.avatar_url}
                    alt={event.name}
                    className="w-7 h-7 rounded object-cover border border-zinc-700 flex-shrink-0"
                  />
                ) : (
                  <div className="w-7 h-7 rounded bg-zinc-700 flex items-center justify-center flex-shrink-0">
                    <span className="text-[10px] font-semibold text-white">
                      {event.name?.substring(0, 2).toUpperCase()}
                    </span>
                  </div>
                )}
                <h1 className="text-sm font-medium text-white truncate">
                  <span className="font-semibold">{event.name}</span>
                  <span className="text-zinc-500 mx-2">•</span>
                  <span className="text-zinc-300">
                    {section === 'overview' ? 'Overview' :
                      section === 'production' ? 'Production' :
                      section === 'schedule' ? 'Schedule' :
                      section === 'team' ? 'Team' :
                      section === 'crew' ? 'Crew' :
                      section === 'travel' ? 'Travel' :
                      section === 'av_technical' ? 'AV Technical' :
                      section === 'resources' ? 'Resources' :
                      navSections.find(s => s.section === section)?.name || 'Event Portal'}
                  </span>
                </h1>
              </div>
            ) : null}

            {event?.google_drive_url && (
              <a
                href={event.google_drive_url}
                target="_blank"
                rel="noopener noreferrer"
                title="Open Google Drive Folder"
                className="flex-shrink-0"
              >
                <Button variant="ghost" size="icon" className="text-zinc-400 hover:text-white hover:bg-zinc-800 h-8 w-8">
                  <img src="/google-drive.png" alt="Google Drive" className="w-4 h-4" />
                </Button>
              </a>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="text-zinc-400 hover:text-white flex-shrink-0">
                  {profile?.avatar_url ? (
                    <img
                      src={profile.avatar_url}
                      alt="User avatar"
                      className="w-7 h-7 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-7 h-7 bg-zinc-700 rounded-full flex items-center justify-center">
                      <span className="text-xs font-medium text-white">
                        {profile?.full_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                      </span>
                    </div>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-zinc-900 border-zinc-800">
                <div className="px-2 py-1.5 text-sm text-zinc-400">
                  <p className="font-medium text-white">
                    {profile?.full_name || user?.email?.split('@')[0] || 'User'}
                  </p>
                  <p className="text-xs text-zinc-500 truncate">
                    {user?.email}
                  </p>
                </div>
                <DropdownMenuSeparator className="bg-zinc-800" />
                {profile?.role === 'admin' && (
                  <DropdownMenuItem asChild className="text-zinc-300 focus:text-white focus:bg-zinc-800 cursor-pointer">
                    <Link to={createPageUrl('AdminUsers')}>
                      <User className="w-4 h-4 mr-2" />
                      Manage Users
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem asChild className="text-zinc-300 focus:text-white focus:bg-zinc-800 cursor-pointer">
                  <Link to={createPageUrl('Settings')}>
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-zinc-800" />
                <DropdownMenuItem
                  onClick={async () => {
                    await supabase.auth.signOut();
                    window.location.href = createPageUrl('Home');
                  }}
                  className="text-zinc-300 focus:text-white focus:bg-zinc-800 cursor-pointer"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {isLoading ? (
          <>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full pt-[calc(3.5rem+2rem)]">
              <div className="space-y-4">
                <Skeleton className="h-8 w-64 bg-zinc-800/50" />
                <Skeleton className="h-32 w-full bg-zinc-800/50" />
                <Skeleton className="h-48 w-full bg-zinc-800/50" />
              </div>
            </div>
          </>
        ) : event ? (
          <>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full pt-[calc(3.5rem+2rem)]">
              <div className="w-full max-w-full min-w-0 overflow-hidden">
                <SectionComponent event={event} eventId={eventId!} permissions={permissions} />
              </div>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center flex-1">
            <p className="text-zinc-500">Event not found</p>
          </div>
        )}
      </div>
    </div>
  );
}
